import { lazy, useMemo } from 'react';
import { Box, Text, Group, Badge, ScrollArea, useMantineTheme, Tooltip } from '@mantine/core';
import { IconTimeline } from '@tabler/icons-react';
import { WidgetProps, ProjectData } from '../types';
import { registerWidget } from '../registry';
import { FadeIn } from '../../../animations';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// ProjectGanttChart Widget Component
// ============================================================================

function ProjectGanttChartWidget({ globalData, isCollapsed }: WidgetProps) {
  const theme = useMantineTheme();
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  if (isCollapsed) return null;

  const projects = globalData?.projects || [];

  // Calculate date range for the chart
  const { chartData, dateRange, totalDays } = useMemo(() => {
    if (projects.length === 0) {
      return { chartData: [], dateRange: { start: new Date(), end: new Date() }, totalDays: 30 };
    }

    // Filter to active projects
    const activeProjects = projects
      .filter((p) => p.status === 'in-progress' || p.status === 'planning')
      .slice(0, 6);

    if (activeProjects.length === 0) {
      return { chartData: [], dateRange: { start: new Date(), end: new Date() }, totalDays: 30 };
    }

    // Find date range
    const allDates = activeProjects.flatMap((p) => [
      new Date(p.startDate),
      new Date(p.dueDate),
    ]);
    const minDate = new Date(Math.min(...allDates.map((d) => d.getTime())));
    const maxDate = new Date(Math.max(...allDates.map((d) => d.getTime())));

    // Add some padding
    const paddedStart = new Date(minDate);
    paddedStart.setDate(paddedStart.getDate() - 3);
    const paddedEnd = new Date(maxDate);
    paddedEnd.setDate(paddedEnd.getDate() + 3);

    const total = Math.ceil((paddedEnd.getTime() - paddedStart.getTime()) / (1000 * 60 * 60 * 24));

    const data = activeProjects.map((project) => {
      const start = new Date(project.startDate);
      const end = new Date(project.dueDate);
      const startOffset = Math.max(0, Math.ceil((start.getTime() - paddedStart.getTime()) / (1000 * 60 * 60 * 24)));
      const duration = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

      return {
        ...project,
        startOffset,
        duration,
        startPercent: (startOffset / total) * 100,
        widthPercent: (duration / total) * 100,
      };
    });

    return {
      chartData: data,
      dateRange: { start: paddedStart, end: paddedEnd },
      totalDays: total,
    };
  }, [projects]);

  const getStatusColor = (status: ProjectData['status']) => {
    const colors: Record<ProjectData['status'], string> = {
      planning: 'gray',
      'in-progress': 'blue',
      'on-hold': 'yellow',
      completed: 'green',
      cancelled: 'red',
    };
    return colors[status];
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Generate month markers
  const monthMarkers = useMemo(() => {
    const markers: Array<{ date: Date; position: number }> = [];
    const current = new Date(dateRange.start);
    current.setDate(1);
    current.setMonth(current.getMonth() + 1);

    while (current <= dateRange.end) {
      const daysFromStart = Math.ceil(
        (current.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
      );
      const position = (daysFromStart / totalDays) * 100;
      if (position > 0 && position < 100) {
        markers.push({ date: new Date(current), position });
      }
      current.setMonth(current.getMonth() + 1);
    }
    return markers;
  }, [dateRange, totalDays]);

  if (chartData.length === 0) {
    return (
      <FadeIn style={{ height: '100%' }}>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: theme.colors.gray[5],
          }}
        >
          <IconTimeline size={32} stroke={1.5} />
          <Text size="sm" mt="xs" style={accessibilityStyles}>
            No active projects to display
          </Text>
        </Box>
      </FadeIn>
    );
  }

  return (
    <FadeIn style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Date range header */}
        <Group justify="space-between" mb="sm">
          <Text size="xs" c="dimmed" style={accessibilityStyles}>
            {formatDate(dateRange.start)}
          </Text>
          <Text size="xs" c="dimmed" style={accessibilityStyles}>
            {formatDate(dateRange.end)}
          </Text>
        </Group>

        {/* Gantt chart */}
        <ScrollArea style={{ flex: 1, minHeight: 0 }}>
          <Box style={{ position: 'relative', minHeight: chartData.length * 45 }}>
            {/* Today marker */}
            {(() => {
              const today = new Date();
              if (today >= dateRange.start && today <= dateRange.end) {
                const todayPosition =
                  ((today.getTime() - dateRange.start.getTime()) /
                    (dateRange.end.getTime() - dateRange.start.getTime())) *
                  100;
                return (
                  <Box
                    style={{
                      position: 'absolute',
                      left: `${todayPosition}%`,
                      top: 0,
                      bottom: 0,
                      width: 2,
                      backgroundColor: theme.colors.red[5],
                      zIndex: 10,
                    }}
                  />
                );
              }
              return null;
            })()}

            {/* Month markers */}
            {monthMarkers.map((marker, index) => (
              <Box
                key={index}
                style={{
                  position: 'absolute',
                  left: `${marker.position}%`,
                  top: 0,
                  bottom: 0,
                  width: 1,
                  backgroundColor: theme.colors.gray[2],
                  zIndex: 1,
                }}
              />
            ))}

            {/* Project bars */}
            {chartData.map((project, index) => (
              <Tooltip
                key={project.id}
                label={
                  <Box>
                    <Text size="xs" fw={500} style={accessibilityStyles}>
                      {project.name}
                    </Text>
                    <Text size="xs" style={accessibilityStyles}>
                      {formatDate(new Date(project.startDate))} -{' '}
                      {formatDate(new Date(project.dueDate))}
                    </Text>
                    <Text size="xs" style={accessibilityStyles}>Progress: {project.progress}%</Text>
                  </Box>
                }
                withArrow
              >
                <Box
                  style={{
                    position: 'absolute',
                    top: index * 45 + 8,
                    left: 0,
                    right: 0,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {/* Project name */}
                  <Box
                    style={{
                      width: 120,
                      flexShrink: 0,
                      paddingRight: 8,
                    }}
                  >
                    <Text size="xs" fw={500} lineClamp={1} style={accessibilityStyles}>
                      {project.name}
                    </Text>
                  </Box>

                  {/* Bar container */}
                  <Box
                    style={{
                      flex: 1,
                      position: 'relative',
                      height: 24,
                      backgroundColor: theme.colors.gray[1],
                      borderRadius: theme.radius.sm,
                    }}
                  >
                    {/* Project bar */}
                    <Box
                      style={{
                        position: 'absolute',
                        left: `${project.startPercent}%`,
                        width: `${project.widthPercent}%`,
                        height: '100%',
                        backgroundColor: theme.colors[getStatusColor(project.status)][3],
                        borderRadius: theme.radius.sm,
                        overflow: 'hidden',
                      }}
                    >
                      {/* Progress fill */}
                      <Box
                        style={{
                          height: '100%',
                          width: `${project.progress}%`,
                          backgroundColor: theme.colors[getStatusColor(project.status)][5],
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Tooltip>
            ))}
          </Box>
        </ScrollArea>
      </Box>
    </FadeIn>
  );
}

// ============================================================================
// Widget Registration
// ============================================================================

registerWidget({
  id: 'project-gantt',
  name: 'Project Timeline',
  description: 'Gantt-style chart showing project timelines and progress',
  icon: IconTimeline,
  category: 'charts',
  component: lazy(() => Promise.resolve({ default: ProjectGanttChartWidget })),
  sizes: {
    base: { cols: 12, rows: 5 },
    sm: { cols: 12, rows: 5 },
    md: { cols: 12, rows: 5 },
    lg: { cols: 8, rows: 5 },
    xl: { cols: 6, rows: 5 },
    tv: { cols: 6, rows: 4 },
  },
  sizeVariants: {
    variants: ['small', 'medium', 'large', 'full'],
    defaultVariant: 'medium',
    variantSizes: {
      small: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 8, rows: 4 },
        lg: { cols: 6, rows: 4 },
        xl: { cols: 4, rows: 4 },
        tv: { cols: 4, rows: 4 },
      },
      medium: {
        base: { cols: 12, rows: 5 },
        sm: { cols: 12, rows: 5 },
        md: { cols: 12, rows: 5 },
        lg: { cols: 8, rows: 5 },
        xl: { cols: 6, rows: 5 },
        tv: { cols: 6, rows: 4 },
      },
      large: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 12, rows: 6 },
        lg: { cols: 10, rows: 6 },
        xl: { cols: 8, rows: 6 },
        tv: { cols: 8, rows: 5 },
      },
      full: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 12, rows: 6 },
        lg: { cols: 12, rows: 6 },
        xl: { cols: 12, rows: 6 },
        tv: { cols: 12, rows: 5 },
      },
    },
  },
  data: {
    sourceType: 'global',
    globalDataKeys: ['projects'],
  },
  hasSettings: true,
});

export default ProjectGanttChartWidget;
